import type {ComponentType} from 'react';
import {Map, List, type RecordOf} from 'immutable';
import type {WebflowCssProperties} from '@packages/systems/core/utils/WebflowCssSubset';
import type {
  Constraints,
  NormalizedConstraints,
} from '@packages/systems/constraints-types';
import type {DynamoBindingProps} from '@packages/systems/dynamo/binding-props';
import type {SharedContextType} from '@packages/systems/core/records/SharedContext';
import type {DefaultBaseStyleConfig} from '@packages/systems/style/types';
import type {
  BindingPathMap,
  BindingPathMapPojo,
} from '@packages/systems/dynamo/binding-types';
import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import type {IconProps, QualifiedPluginIcon} from './Plugins/PluginIconTypes';
import type {Type} from './DataTypeDefinition';
import type {DisplayName} from './DisplayName';
import {applyBrand, type BrandedType} from '@packages/utilities/type-safety';
import {RenderTrackingContextItem} from '@packages/systems/element-tracking-types';

import {EvaluatedNodeData} from '@packages/utilities/data-utils';
import {type Lazy} from '@packages/utilities/lazy';
import {type ElementTopLevelPropBindingInfo} from '@packages/systems/dynamo/wfdl';

export type ParamsType = {
  type: 'StylesMap';
  excludeInherited: boolean;
  filterProperties: string[];
};

export type ParamsTypeConfig = {
  [key: string]: ParamsType;
};

/**
 * LockedStyleType
 */
type LockedStyleType = {
  /**
   * If `when` is true, the style will always be locked.
   */
  when:
    | boolean
    | {
        [key: string]: any;
      }; // In an ideal world, [string] would be [<$Keys<Props>>];
  /**
   * The message to be displayed when the user attempts to change this style.
   */
  message: string;
};

/**
 * Lock styles by property, the key is the css property name.
 */
export type LockedStyles = Partial<
  Record<keyof WebflowCssProperties, LockedStyleType>
>;

type PropType = PropEnum; // TODO Add PropSwitch / PropBoolean, PropRange, etc.

export const EnumType = 'enum';
export type PropEnum = {
  type: typeof EnumType;
  values: Array<string>;
};

export type AtomConfigProps = {
  [key: string]: PropType;
};

/**
 * DerivedFromNode is used when an icon needs to be computed with some data that lives on the node. For example, showing a different h<number> icon based on the Heading node's `tag` data.
 */
type DerivedFromElementIcon = (
  arg1: ExpressionElement,
  arg2: Expression
) => React.ComponentType<IconProps> | QualifiedPluginIcon;

type DerivedIcon = {
  type: 'Derived';
  getFromElement: DerivedFromElementIcon;
};

/**
 * The Component Icon renders a React Component directly.
 *
 * ```javascript
 * import AnimationSmall from './element-div.svg';
 *
 * // In AtomConfig
 * icon: {
 *   type: 'Component',
 *   value: qualify([Plugins.name('Basic'), iconName('Div')]),
 * },
 * ```
 */
type ComponentIcon = {
  type: 'Component';
  value: any;
};

/**
 * Icon's used by core.
 */
export type Icon = DerivedIcon | ComponentIcon;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type AtomConfig = {
  /**
   * className allows you specify the base-level class name that will be used when this Atom's styles are compiled. This className is used as is
   * except in the case of conditional styles, in which case the same conditional style selectors will be appended.
   *
   * Typically this should only be used if you are migrating styles from Bootflow *and* you believe custom code users rely on these selectors.
   *
   * If a className is not provided core will leverage the plugin & atom names to create a namespaced class automatically.
   *
   * Example migration of styles from bootflow to list.
   *
   * ```javascript
   * const MyAtom = {
   *    className: 'w-my-atom'
   *    style: {...}
   * }
   *
   * // CSS Compilation'.w-my-atom { ... }'
   * ```
   */
  className?: string;
  /**
   * Constraints are rules about the Atom's relationship to other Atoms and enforces that all those rules are satisfied when a user tries to update the design.
   * This API offers a way to make sure the site's design is valid with semantics at a higher level than HTML tags.
   *
   * Constraints support components, by ensuring relationship rules are enforce by evaluating components before enforcing rules.
   *
   * ### Constraint Behavior
   *
   * A constraint must be met in order for the tree to be valid. If the tree a potential tree is invalid the action will be prevented and change won't be accepted.
   *
   * ```javascript
   * CommerceAddToCartQuantityInput.config = {
   *   constraints: {
   *     ancestors: [{is: 'CommerceAddToCartForm' }],
   *   },
   * };
   *
   * // Node Tree
   * {
   *   type: CommerceAddToCartForm,
   *   children: [
   *     {
   *       type: Block,
   *       children: [
   *         { type: CommerceAddToCarQuantityInput }
   *       ]
   *     }
   *   ]
   * }
   * ```
   *
   * In this case the constraints would be satisfied because the
   * `CommerceAddToCartQuantityInput` has an ancestor that is a
   * `CommerceAddToCartForm`.
   *
   * The `children` & `parent` keys can be used to describe direct relationships that only need to be enforced a single level deep. Consider a `<li>` tag. A `<li>` tag must have a direct ancestor of an `<ul>` or `<ol>`. Similarly a `<ul>` & `<li>` tag should `Forbid` everything that isn't a `<li>` but *only* a single a level deep.
   *
   * ```html
   * <!-- ✅ -->
   * <ul>
   *   <li />
   * </ul>
   *
   * <!-- 🛑 -->
   * <ul>
   *   <div>
   *     <li />
   *   </div>
   * </ul>
   *
   * <!-- ✅ -->
   * <ul>
   *   <li><div /></li>
   * </ul>
   *
   * <!-- 🛑 -->
   * <ul>
   *   <ul />
   * </ul>
   *
   * <!-- ✅ -->
   * <ul>
   *   <li>
   *     <ul />
   *   </li>
   * </ul>
   * ```
   *
   * An example of using `parent` & `children` to enforce these relationships looks like this. In Webflow, the `List` and `ListItem` types are used to represent `<ul>`, `<ol>` & `<li>` types.
   *
   *  ```javascript
   * // `List` must only allow direct descendants that are `ListItem`s!
   * List.config = {
   *   constraints: {
   *     children: [{
   *       // Expressing an allowlist is coming. For now, we'll use the UnsafeConstraintEscapeHatch.
   *       is: {
   *         check: (node) => node.type !== 'ListItem',
   *         name: 'Non-List Item'
   *       },
   *       rule: 'Forbid',
   *     }],
   *   },
   * };
   *
   *
   * // `ListItem` must have a direct ancestor that is a `List`!
   * ListItem.config  = {
   *   constraints: {
   *     parent: [{
   *       is: 'List',
   *       rule: 'ExactlyOne',
   *     }],
   *   },
   * };
   * ```
   */
  constraints?: Constraints;
  /**
   * `hideAncestorSettings` is used in the event we'd like to hide ancestor
   * settings when an element is selected. It defaults to `false` in SettingsTab.jsx.
   *
   *
   * For example:
   *
   * hideAncestorSettings: true,
   */
  hideAncestorSettings?: boolean;
  /**
   * `showSettingsInDescendants` allows you to display an element's settings for
   *  all nested elements. For example, if we set within `TabsWrapper` Atom config:
   *
   * showSettingsInDescendants: true,
   *
   * with the following element structure:
   *
   * Tabs
   *  -- Tabs Menu
   *    -- Tab Link
   * -- Tabs Content
   *    -- Tab Pane
   *      -- Rich Text Block
   *
   * The settings for `Tabs` appears within the `Rich Text Block`. The settings for `Tabs`
   * will appear for any added elements nested under `Tabs`.
   * Note: This is at most additive, and will not result in redundant rendered settings
   * with other settings configurations, e.g. `constraints`.
   */
  showSettingsInDescendants?: boolean;
  /**
   * DataTypes allow you provide settings & other data related information for your Atom! The `dataType` should reflect the Element's `data` shape.
   *
   * Type is designed to be progressively adopted, it will ignore fields that exist in `element.data` that aren't expressed in the `dataType`.
   *
   * For example, to mirror the `element.data`: `{ tag: 'ul' | 'ol', list: { unstyled: true | false } }`
   *
   * ```javascript
   * const List = {
   *   dataType: TRecord({
   *     children: {type: 'List', of: {type: 'Element'}},
   *     tag: withMeta(
   *       {label: 'Type'},
   *       TEnum(
   *         {
   *           ul: 'Unordered',
   *           ol: 'Ordered',
   *         },
   *       )
   *     ),
   *     list: TRecord({
   *       unstyled: withMeta(
   *         {name: 'No bullets'},
   *         withSystem(
   *            {label: 'Style'},
   *            TBoolean
   *         )
   *       ),
   *     }),
   *   }),
   * }
   * ```
   *
   * Webflow core will render the Settings interface to configure the Atom accordingly.
   */
  dataType?: Type;
  /**
   * The displayName used to represent the Atom in the Designer. Elements can take on new names such as the class they have applied, however, the Atom name is generic for all elements of a given Atom type.
   */
  displayName?: DisplayName;
  /**
   * URL to an external script to be added to the `<head>` of the document. If it is a script provided by the Atom, see the plugin JsModule APIs.
   * ```javascript
   * const StripeButton = {
   *   headScript: 'https://js.stripe.com/v3/',
   * };
   * ```
   */
  headScript?: string;
  /**
   * The icon used to represent that Atom in the Designer. For example in the Navigator or Breadcrumbs.
   */
  icon?: Icon;
  /**
   * Prevent certain styles from being changed by the user. This does *not* guarantee those styles will not change but it does prevent the user from changing them directly in the Style Panel.
   *
   * Lock the float property for the Body element.
   *
   * ```javascript
   * const Body = {
   *  lockedStyles: {
   *    float: {when: true, message: 'The body can’t be floated.'},
   *   }
   * }
   * ```
   */
  lockedStyles?: LockedStyles;
  params?: ParamsTypeConfig;
  /**
   * Path to a value in the node's data that is to be leveraged by Dynamo as a template during token binding.
   */
  pathToReplaceableString?: Array<string>;
  // TODO: other supported Webflow prop types should form a union type here: WebflowEnumType | WebflowBoolType...
  props?: AtomConfigProps;
  /**
   * The showEmpty flag is used to indicate to core that if this element is empty, it should be given some pseudo dimensions and visibility so that a user can easily drag nodes into and select it on the Canvas.
   *
   * Notably, showEmpty does nothing in Previewed or Rendered sites.
   */
  showEmpty: boolean;
  /**
   * Avoid using this! In cases where a non-zero font size is needed (such as for drawing list item bullets) the standard aboveEmpty indication above cannot be used.
   */
  showEmptyAsBlock?: boolean;
  /**
   * This how to declare constant bindings to Dynamo. When a node is bound to something in a dynamic context it has some instructions for how to find that information, e.g. `{ id: 'id', slug: 'id', type: 'PlainText' }`. This is the same data structure that Dynamo adds to the node data when the user sets up bindings in the UI.
   *
   * Static bindings _always_ apply to a given node, and can't be edited/removed by the user. Bindings currently rely on an existing Collection schema, so this API is typically used with some backend coordination.
   *
   * ```javascript
   * const staticBindings = Map({
   *  value: List([
   *    Map({
   *      slug: 'customerEmail', // Bind this node's `value` to to the `customerEmail` found in its context.
   *      type: 'PlainText',
   *    }),
   *  ]),
   * });
   *
   * const EmailInputAtom = {
   *  staticBindings: Map({
   *    value: List([
   *      Map({
   *        slug: 'customerEmail', // Bind this node's `value` to to the `customerEmail` found in its context.
   *        type: 'PlainText',
   *      }),
   *    ]),
   *  }),
   *  render: (props) => {
   *   // Bindings will be resolved before ending up in here, for example in `generateAttributes`
   *  }
   * }
   * ```
   */
  staticBindings?: BindingPathMap;
  /**
   * Provide default styles for an Atom.
   * ```javascript
   *   // In `Atom.jsx`
   *   const config = Atom.create({
   *     ... // Other config
   *     style: {
   *       base: {
   *         main: {
   *           noPseudo: {
   *             color: createRgbaColor('f00'),
   *           },
   *         },
   *       },
   *     },
   *   });
   * ```
   */
  style?: DefaultBaseStyleConfig;
  /**
   * This value is used to indicate what the nearest ancestors `stateAtom` value should be updated to when this node is selected in the navigator or on the Canvas.
   *
   * ```javascript
   * const DynamoEmpty = {
   *   uiStateOnSelected: 'empty' // Set the UI State to empty when this node is selected.
   * }
   * ```
   * When the above Dynamo Empty (Empty Collection List) is selected in the navigator. Core will walk up each ancestor till it finds an instance of the plugin's `stateAtom`. In the case of the Dynamo plugin:
   *
   * ```javascript
   * // Dynamo Plugin index.js
   * export const stateAtom = 'DynamoWrapper';
   * ```
   *
   * Once this node is found, that instance of the DynamoWrapper's temporary state, `node.data.temp.state`, will be updated to the value 'empty' which was declared on the selected node's Atom property `uiStateOnSelected`.
   * Data stored in `temp` is client only and is not persisted.
   */
  uiStateOnSelected?: string;
  /**
   * This value is used to indicate that the Atom should render a `data-wf-native-id-path` attribute on the element.
   * The native id path is used to identify an instance of an element, with the last value in the path being the unique identifier for the element.
   * The prior values in the path are used to identify the element's component ancestors.
   *
   * Note this is only supported in the server-side renderer. Designer, Editor, and Preview will not render this attribute.
   */
  autoEngagementTrackingAttribute?: boolean;
  /**
   * The Atom Render function is a React Component. However, it may not behave as you expect!
   *
   * AtomRender functions are run locally on the client in Preview, Designer & Editor. However, the published site deployment is rendered on the server and stored in a cache. If the page is dynamic, it will be rendered on the server
   * still it just will happen to be updated on each request.
   */
  render: AtomRender;
};

export type NormalizedAtomConfig = AtomConfig & {
  atomKey: string;
  pluginKey: string;
  displayName: DisplayName;
  icon: Icon;
  constraints: NormalizedConstraints;
  pathToReplaceableString: Array<string>;
  stateAtom: string | undefined;
  dataType: Type;
};

export type NormalizedAtomConfigMap = {
  [key: string]: NormalizedAtomConfig;
};

type CommonAtomConfigShape = {
  className?: string;
  constraints?: Constraints;
  hideAncestorSettings?: boolean;
  showSettingsInDescendants?: boolean;
  displayName?: DisplayName;
  headScript?: string;
  icon?: Icon;
  lockedStyles?: LockedStyles;
  params?: ParamsTypeConfig;
  pathToReplaceableString?: Array<string>;
  props?: AtomConfigProps;
  showEmpty: boolean;
  showEmptyAsBlock?: boolean;
  staticBindings?: BindingPathMap;
  style?: DefaultBaseStyleConfig;
  uiStateOnSelected?: string;
  autoEngagementTrackingAttribute?: boolean;
  render: AtomRender;
};

export type AtomConfigShape = CommonAtomConfigShape & {
  fields: {
    [key: string]: Type;
  };
};

export type CustomAtomConfigShape = CommonAtomConfigShape & {
  dataType?: Type;
};

export type StyleAttributeImm = Map<string, string>;
export type StyleAttributeObject = {
  [key: string]: string;
};

export type AttributesObject = {
  style?: StyleAttributeObject;
  [key: string]: string | StyleAttributeObject | undefined;
};

export type AttributesMap = Map<string, string | StyleAttributeImm>;
export type ClassesList = List<string>;

export type GenerateAttributes = (
  attributes: AttributesMap,
  classes: ClassesList
) => AttributesObject;

export type ContextType = Map<string, any> | RecordOf<any>;
export type PluginsContext = Map<string, ContextType>;
export type EnvironmentContextType = any; // TODO: Figure out this type
// WDFL doesn’t know about the shape of context, since it could contain
// evaluated values. We can only type it as any or mixed at the Flow level.
export type PluginComponentContextType = any;

export type AtomRenderProps = {
  elementId: string;
  nativeIdPath: string[];
  attributes: AttributesMap;
  classes: ClassesList;
  data: EvaluatedNodeData;
  /** Legacy dyn/search binding paths, supplied by the dynamo layer for binding-aware rendering. */
  legacyDynamoBindingPaths: BindingPathMapPojo | undefined;

  /**
   * Information about the top level bindings for a given element. Useful to signal
   * bindings information that are lost once binding data is evaluated. Check the
   * Image Atom (@link packages/systems/core/plugins/Basic/Image.tsx) for an example
   * of usage.
   */
  fieldBindings: Lazy<ElementTopLevelPropBindingInfo>;
  renderChild: ChildRender;
  generateAttributes: GenerateAttributes;
  context: ContextType;
  environmentContext: EnvironmentContextType;
  sharedContext: SharedContextType;
} & DynamoBindingProps;

export type ChildRender = (
  child: EvaluatedNodeData,
  pluginContext?: ContextType,
  key?: string,
  instanceIndex?: number,
  trackingContextItem?: RenderTrackingContextItem
) => React.ReactElement<unknown> | null | string;

export type ChildrenRender = (
  pluginContext?: ContextType,
  key?: string
) =>
  | Array<React.ReactElement<unknown>>
  | Immutable.Iterable<number, React.ReactElement<unknown>>
  | null;

export type AtomRender = BrandedType<
  'AtomRender',
  ComponentType<AtomRenderProps>
>;

function createRender(Component: ComponentType<AtomRenderProps>): AtomRender {
  return applyBrand<AtomRender>(Component);
}

/**
 * createCustom is a thinly wrapped constructor function that
 * allows a developer complete control over an atom's configuration.
 */
export const create = (shape: CustomAtomConfigShape): AtomConfig => shape;

/**
 * Unwraps the opaque type. Typically useful when needing to share atom configurations.
 */
export const getConfigShape = (config: AtomConfig): CustomAtomConfigShape =>
  config;

/**
 * Unwraps an AtomConfig's wfdl type.
 */
export const getType = (config: AtomConfig): undefined | Type =>
  config.dataType;

export const forAtomCreation = {
  create,
  getConfigShape,
  getType,
  createRender,
} as const;
